import { z } from 'zod'

import { idSchema, msTimestampSchema, PrivacyLevelEnum } from './common.ts'
import { TikTokUserInfoSchema } from './tiktok-user-info.ts'

export const AccountRecoveredProfileSchema = TikTokUserInfoSchema.extend({
  displayName: z.string().min(1),
  username: z.string().min(1).nullable(),
  followerCount: z.number().int().nonnegative().nullable(),
  connectionGeneration: z.number().int().nonnegative()
})

export const ChannelPlatformEnum = z.enum(['tiktok'])
export type ChannelPlatform = z.infer<typeof ChannelPlatformEnum>

export const RampUpCounterSourceEnum = z.enum(['user_approved', 'any_published'])
export type RampUpCounterSource = z.infer<typeof RampUpCounterSourceEnum>

export const MediaRequirementsSchema = z
  .object({
    requiredAspectRatio: z
      .object({
        width: z.number().int().positive(),
        height: z.number().int().positive()
      })
      .strict(),
    maxDurationSeconds: z.number().int().positive()
  })
  .strict()
export type MediaRequirements = z.infer<typeof MediaRequirementsSchema>

export const DEFAULT_MEDIA_REQUIREMENTS: MediaRequirements = {
  requiredAspectRatio: { width: 9, height: 16 },
  maxDurationSeconds: 60
}

// RAL-217: pacing, privacy, media limits and auto-publish trust belong to the channel, not the
// campaign, so a workspace campaign can target several channels with different limits.
export const ChannelSettingsSchema = z.object({
  maxPostsPerDay: z.number().int().positive().default(2),
  minHoursBetweenPosts: z.number().int().nonnegative().default(3),
  // Refined in PR 5 alongside promotionContext.
  blackoutWindows: z.array(z.unknown()).default([]),
  postsBeforeAutoPublish: z.number().int().nonnegative().default(5),
  rampUpCounterSource: RampUpCounterSourceEnum.default('user_approved'),
  privacyLevel: PrivacyLevelEnum.default('SELF_ONLY'),
  requiredAspectRatio: MediaRequirementsSchema.shape.requiredAspectRatio.default(
    DEFAULT_MEDIA_REQUIREMENTS.requiredAspectRatio
  ),
  maxDurationSeconds: MediaRequirementsSchema.shape.maxDurationSeconds.default(
    DEFAULT_MEDIA_REQUIREMENTS.maxDurationSeconds
  )
})
export type ChannelSettings = z.infer<typeof ChannelSettingsSchema>

// "HH:MM" in the campaign timezone, end exclusive, matching backend/services/agent/rules.ts's BlackoutWindow.
const clockSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)
export const BlackoutWindowSchema = z.object({ start: clockSchema, end: clockSchema })
export type BlackoutWindow = z.infer<typeof BlackoutWindowSchema>

// RAL-229: what an admin edits on a channel. Aspect ratio and max duration are TikTok's limits for the
// account, so they stay out of the body.
export const ChannelSettingsUpdateBodySchema = z
  .object({
    maxPostsPerDay: z.number().int().positive().max(20),
    minHoursBetweenPosts: z.number().int().nonnegative().max(24),
    blackoutWindows: z.array(BlackoutWindowSchema).max(10),
    privacyLevel: PrivacyLevelEnum,
    postsBeforeAutoPublish: z.number().int().nonnegative().max(100),
    rampUpCounterSource: RampUpCounterSourceEnum
  })
  .strict()
export type ChannelSettingsUpdateBody = z.infer<typeof ChannelSettingsUpdateBodySchema>

// Channel settings as the settings page reads them: stored blackout windows are `unknown[]` at rest, so
// the response narrows them to the windows that parse.
export const ChannelSettingsViewSchema = ChannelSettingsSchema.extend({
  blackoutWindows: z.array(BlackoutWindowSchema)
})
export type ChannelSettingsView = z.infer<typeof ChannelSettingsViewSchema>

export const AccountSchema = z.object({
  id: idSchema,
  // "Connected by": the user who first connected the channel. Not an owner and not an authorization
  // input (RAL-80); workspace admins act on the channel. Kept under this name because it is the
  // stored row field and the account-user label.
  userId: idSchema,
  // Account.create requires it (RAL-217: a channel belongs to exactly one workspace). Still
  // nullable at rest so a row that predates the RAL-77 backfill parses instead of throwing.
  workspaceId: idSchema.nullable().default(null),
  platform: ChannelPlatformEnum.default('tiktok'),
  tiktokOpenId: z.string().min(1),
  displayName: z.string().min(1),
  // True for a provider-supplied name; a display name is not necessarily a username.
  displayNameIsReal: z.boolean().default(false),
  username: z.string().min(1).nullable().default(null),
  recoveredProfile: AccountRecoveredProfileSchema.nullable().default(null),
  // KMS/AES-encrypted refresh token ciphertext (base64-encoded). Never log this value.
  encryptedRefreshToken: z.string().min(1),
  // RAL-17: encrypted the same way as encryptedRefreshToken (AES-256-GCM via
  // backend/services/crypto.ts) — a live bearer credential scoped to video.publish/video.upload
  // is not safe to store in plaintext just because it's shorter-lived (24h) than the refresh
  // token. Never log this value.
  encryptedAccessToken: z.string().min(1),
  accessTokenExpiresAt: msTimestampSchema,
  scopes: z.array(z.string()).default([]),
  // RAL-214: the scope set Rally asked for at the consent that produced `scopes`. A requested
  // scope absent from `scopes` was declined by the creator, not added later, so it isn't drift.
  // Empty on rows written before RAL-214, which makes every missing scope count as drift once.
  requestedScopes: z.array(z.string()).default([]),
  followerCount: z.number().int().nonnegative().nullable().default(null),
  // RAL-57/RAL-115: set when a token refresh fails with a non-retryable TikTokError (the
  // refresh token itself is dead, e.g. TikTok's `invalid_grant`) — refresher.ts stops
  // scheduling further attempts for this account once this is set. Cleared on a successful
  // reconnect (saveAccountTokens), never by the refresher itself.
  degradedAt: msTimestampSchema.nullable().default(null),
  // Defaults for rows written before RAL-53. A successful reconnect increments this value.
  connectionGeneration: z.number().int().nonnegative().default(0),
  // RAL-104: set the first time this account's one-time setup-handoff panel is shown and
  // dismissed. Persisted (not client-side storage) so "shown once and never returns" holds
  // across devices and cleared browser storage, not just the current browser.
  handoffShownAt: msTimestampSchema.nullable().default(null),
  ...ChannelSettingsSchema.shape,
  createdAt: msTimestampSchema,
  updatedAt: msTimestampSchema
})

export type AccountRow = z.infer<typeof AccountSchema>

// isRealHandle is true only when the label comes from a verified TikTok username.
export const AccountDetailSchema = z.object({
  id: idSchema,
  displayName: z.string().min(1),
  isRealHandle: z.boolean(),
  active: z.boolean(),
  connectedAt: msTimestampSchema,
  accessTokenExpiresAt: msTimestampSchema,
  degradedSince: msTimestampSchema.nullable(),
  lastPublishedAt: msTimestampSchema.nullable(),
  clipsStored: z.number().int().nonnegative(),
  publishedRecords: z.number().int().nonnegative(),
  metricsSnapshots: z.number().int().nonnegative(),
  scheduledCount: z.number().int().nonnegative(),
  followerCount: z.number().int().nonnegative().nullable(),
  settings: ChannelSettingsViewSchema,
  // Published posts on this channel that a person approved, the `user_approved` trust counter.
  approvedPublishedCount: z.number().int().nonnegative()
})

export type AccountDetail = z.infer<typeof AccountDetailSchema>

export const AccountsListResponseSchema = z.object({
  accounts: z.array(AccountDetailSchema)
})

export type AccountsListResponse = z.infer<typeof AccountsListResponseSchema>

export const AccountDeletionMarkerSchema = z.object({
  accountId: idSchema,
  deletingAt: msTimestampSchema
})

export type AccountDeletionMarker = z.infer<typeof AccountDeletionMarkerSchema>

export const AccountDegradationClaimSchema = z.object({
  accountId: idSchema,
  connectionGeneration: z.number().int().nonnegative(),
  degradedAt: msTimestampSchema
})

export type AccountDegradationClaim = z.infer<typeof AccountDegradationClaimSchema>

export const AccountLegacyDegradationClaimSchema = z.object({
  accountId: idSchema,
  degradedAt: msTimestampSchema
})

export type AccountLegacyDegradationClaim = z.infer<typeof AccountLegacyDegradationClaimSchema>
